/**
 * Adapted from PlayCanvas Engine's scripts/esm/gsplat/gsplat-text.mjs at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */

import * as THREE from 'three'
import type { GSplatResourceLease } from '@/plugins/gsplat/GSplatHandleBinding'
import type { GSplatGraphicsAdapter } from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import { GSplatFormat } from '@/plugins/gsplat/lod/GSplatFormat'
import type {
  GaussianSplatHandle,
  GaussianSplatLoadRequest,
} from '@/plugins/gsplat/types/PluginTypes'
import { GSplatContainer } from '@/plugins/gsplat/unified/resource/GSplatContainer'
import { getLoggerManager } from '@/shared/logging'
import type { AssetId, Disposable } from '@/shared/types/core'

const log = getLoggerManager().getLogger('gsplat:text')

export interface GSplatTextCanvasContext {
  font: string
  textBaseline: CanvasTextBaseline
  textAlign: CanvasTextAlign
  lineWidth: number
  strokeStyle: string | CanvasGradient | CanvasPattern
  fillStyle: string | CanvasGradient | CanvasPattern
  measureText(text: string): { readonly width: number }
  clearRect(x: number, y: number, width: number, height: number): void
  strokeText(text: string, x: number, y: number): void
  fillText(text: string, x: number, y: number): void
  getImageData(sx: number, sy: number, sw: number, sh: number): ImageData
}

export interface GSplatTextCanvas {
  width: number
  height: number
  getContext(contextId: '2d'): GSplatTextCanvasContext | null
}

export interface GSplatTextRuntime {
  readonly graphicsAdapter: GSplatGraphicsAdapter
  createWithResource(
    request: Readonly<GaussianSplatLoadRequest>,
    resource: GSplatResourceLease
  ): GaussianSplatHandle
  replaceWithResource(
    id: AssetId,
    resource: GSplatResourceLease,
    metadata?: { readonly url?: string; readonly name?: string }
  ): GaussianSplatHandle
  mount(handle: GaussianSplatHandle): void
  disposeAsset(id: AssetId): void
}

export interface GSplatTextOptions {
  readonly id?: AssetId
  readonly name?: string
  readonly text?: string
  readonly fontSize?: number
  readonly fontFamily?: string
  readonly fillStyle?: string
  readonly strokeStyle?: string
  readonly strokeWidth?: number
  readonly padding?: number
  readonly maxCanvasDimension?: number
  readonly canvasFactory?: () => GSplatTextCanvas
}

export type GSplatTextErrorCode =
  | 'GSPLAT_TEXT_DISPOSED'
  | 'GSPLAT_TEXT_INVALID_FIELD'
  | 'GSPLAT_TEXT_CANVAS_UNAVAILABLE'
  | 'GSPLAT_TEXT_CONTEXT_UNAVAILABLE'
  | 'GSPLAT_TEXT_MEASURE_FAILED'
  | 'GSPLAT_TEXT_INVALID_DIMENSIONS'
  | 'GSPLAT_TEXT_DRAW_FAILED'
  | 'GSPLAT_TEXT_READBACK_FAILED'
  | 'GSPLAT_TEXT_ALLOCATION_FAILED'
  | 'GSPLAT_TEXT_FILL_FAILED'
  | 'GSPLAT_TEXT_UPLOAD_FAILED'
  | 'GSPLAT_TEXT_UPDATE_FAILED'
  | 'GSPLAT_TEXT_COMMIT_FAILED'
  | 'GSPLAT_TEXT_STALE_SNAPSHOT'

export class GSplatTextError extends Error {
  constructor(
    readonly code: GSplatTextErrorCode,
    message: string,
    readonly cause?: unknown
  ) {
    super(message)
    this.name = 'GSplatTextError'
  }
}

interface GSplatTextFields {
  readonly text: string
  readonly fontSize: number
  readonly fontFamily: string
  readonly fillStyle: string
  readonly strokeStyle: string
  readonly strokeWidth: number
  readonly padding: number
}

function defaultCanvasFactory(): GSplatTextCanvas {
  if (typeof document === 'undefined') {
    throw new GSplatTextError(
      'GSPLAT_TEXT_CANVAS_UNAVAILABLE',
      'GSplatText 当前环境不能创建 canvas'
    )
  }
  return document.createElement('canvas')
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

function fieldsEqual(left: GSplatTextFields, right: GSplatTextFields): boolean {
  return (
    left.text === right.text &&
    left.fontSize === right.fontSize &&
    left.fontFamily === right.fontFamily &&
    left.fillStyle === right.fillStyle &&
    left.strokeStyle === right.strokeStyle &&
    left.strokeWidth === right.strokeWidth &&
    left.padding === right.padding
  )
}

/** FrameDriver Update phase 驱动的 backend-neutral Canvas text generator。 */
export class GSplatText implements Disposable {
  text: string
  fontSize: number
  fontFamily: string
  fillStyle: string
  strokeStyle: string
  strokeWidth: number
  padding: number

  private readonly id?: AssetId
  private readonly name: string
  private readonly maxCanvasDimension: number
  private readonly canvasFactory: () => GSplatTextCanvas
  private formatValue: GSplatFormat | null = null
  private handleValue: GaussianSplatHandle | null = null
  private stagedHandle: GaussianSplatHandle | null = null
  private stagedContainer: GSplatContainer | null = null
  private committedFields: GSplatTextFields | null = null
  private hasCommittedFields = false
  private generation = 0
  private initialized = false
  private disposed = false
  private errorValue: GSplatTextError | null = null

  constructor(
    private readonly runtime: GSplatTextRuntime,
    options: GSplatTextOptions = {},
    private readonly onDispose?: (generator: GSplatText) => void
  ) {
    this.id = options.id
    this.name = options.name ?? 'GSplatText'
    this.text = options.text ?? ''
    this.fontSize = options.fontSize ?? 64
    this.fontFamily = options.fontFamily ?? 'sans-serif'
    this.fillStyle = options.fillStyle ?? '#ffffff'
    this.strokeStyle = options.strokeStyle ?? 'rgba(0,0,0,0.85)'
    this.strokeWidth = options.strokeWidth ?? 0
    this.padding = options.padding ?? 12
    this.maxCanvasDimension =
      options.maxCanvasDimension ?? runtime.graphicsAdapter.limits.maxTextureDimension2D
    this.canvasFactory = options.canvasFactory ?? defaultCanvasFactory
  }

  get handle(): GaussianSplatHandle | null {
    return this.handleValue
  }

  get format(): GSplatFormat | null {
    return this.formatValue
  }

  get lastError(): GSplatTextError | null {
    return this.errorValue
  }

  initialize(): void {
    this.assertActive()
    if (this.initialized) return
    this.initialized = true
  }

  postUpdate(): void {
    this.assertActive()
    if (!this.initialized) this.initialize()
    let fields: GSplatTextFields
    let rebuildStarted = false
    try {
      fields = this.snapshotFields()
      if (
        this.hasCommittedFields &&
        this.committedFields &&
        fieldsEqual(this.committedFields, fields)
      ) {
        this.errorValue = null
        return
      }
      rebuildStarted = true
      this.rebuildFromFields(fields)
    } catch (error) {
      const structured = this.toError(error, 'GSPLAT_TEXT_COMMIT_FAILED', '文字重建失败')
      if (!rebuildStarted) this.errorValue = structured
      log.warn(`GSplatText 重建失败: code=${structured.code}, reason=${structured.message}`)
    }
  }

  /** 对齐上游 _rebuildFromText；可供宿主显式重建，正常逐帧入口仍是 postUpdate。 */
  _rebuildFromText(): GaussianSplatHandle | null {
    this.assertActive()
    if (!this.initialized) this.initialize()
    return this.rebuildFromFields(this.snapshotFields())
  }

  private rebuildFromFields(fields: GSplatTextFields): GaussianSplatHandle | null {
    const operationGeneration = ++this.generation
    try {
      if (!fields.text) {
        this.commitEmpty(fields, operationGeneration)
      } else {
        this.rebuildText(fields, operationGeneration)
      }
      this.assertSnapshot(fields, operationGeneration)
      this.committedFields = fields
      this.hasCommittedFields = true
      this.errorValue = null
      return this.handleValue
    } catch (error) {
      const structured = this.toError(error, 'GSPLAT_TEXT_COMMIT_FAILED', '文字重建失败')
      if (this.generation === operationGeneration) this.errorValue = structured
      throw structured
    }
  }

  destroy(): void {
    if (this.disposed) return
    this.disposed = true
    this.generation++
    let firstError: unknown
    try {
      this.stagedContainer?.destroy()
    } catch (error) {
      firstError = error
    }
    this.stagedContainer = null
    const handle = this.handleValue
    const stagedHandle = this.stagedHandle
    this.handleValue = null
    this.stagedHandle = null
    const disposeHandle = (target: GaussianSplatHandle | null) => {
      if (!target) return
      try {
        this.runtime.disposeAsset(target.id)
      } catch (error) {
        firstError ??= error
      }
    }
    disposeHandle(handle)
    if (stagedHandle !== handle) disposeHandle(stagedHandle)
    try {
      this.onDispose?.(this)
    } catch (error) {
      firstError ??= error
    }
    if (firstError) throw firstError
  }

  dispose(): void {
    this.destroy()
  }

  private snapshotFields(): GSplatTextFields {
    if (
      typeof this.text !== 'string' ||
      typeof this.fontFamily !== 'string' ||
      typeof this.fillStyle !== 'string' ||
      typeof this.strokeStyle !== 'string'
    ) {
      throw new GSplatTextError(
        'GSPLAT_TEXT_INVALID_FIELD',
        'GSplatText text/fontFamily/fillStyle/strokeStyle 必须是字符串'
      )
    }
    if (!Number.isFinite(this.fontSize) || this.fontSize <= 0) {
      throw new GSplatTextError(
        'GSPLAT_TEXT_INVALID_FIELD',
        `GSplatText fontSize 必须是有限正数: ${this.fontSize}`
      )
    }
    if (!Number.isFinite(this.strokeWidth) || this.strokeWidth < 0) {
      throw new GSplatTextError(
        'GSPLAT_TEXT_INVALID_FIELD',
        `GSplatText strokeWidth 必须是有限非负数: ${this.strokeWidth}`
      )
    }
    if (!Number.isFinite(this.padding) || this.padding < 0) {
      throw new GSplatTextError(
        'GSPLAT_TEXT_INVALID_FIELD',
        `GSplatText padding 必须是有限非负数: ${this.padding}`
      )
    }
    if (!Number.isSafeInteger(this.maxCanvasDimension) || this.maxCanvasDimension <= 0) {
      throw new GSplatTextError(
        'GSPLAT_TEXT_INVALID_DIMENSIONS',
        `GSplatText maxCanvasDimension 非法: ${this.maxCanvasDimension}`
      )
    }
    return Object.freeze({
      text: this.text,
      fontSize: this.fontSize,
      fontFamily: this.fontFamily,
      fillStyle: this.fillStyle,
      strokeStyle: this.strokeStyle,
      strokeWidth: this.strokeWidth,
      padding: this.padding,
    })
  }

  private rebuildText(fields: GSplatTextFields, operationGeneration: number): void {
    const strokeWidth =
      fields.strokeWidth > 0 ? fields.strokeWidth : Math.max(2, Math.round(fields.fontSize * 0.08))
    let canvas: GSplatTextCanvas
    try {
      canvas = this.canvasFactory()
    } catch (error) {
      throw this.toError(error, 'GSPLAT_TEXT_CANVAS_UNAVAILABLE', '创建 canvas 失败')
    }

    let context: GSplatTextCanvasContext | null
    try {
      context = canvas.getContext('2d')
    } catch (error) {
      throw new GSplatTextError(
        'GSPLAT_TEXT_CONTEXT_UNAVAILABLE',
        `GSplatText 创建 2D canvas context 失败: ${errorMessage(error)}`,
        error
      )
    }
    if (!context) {
      throw new GSplatTextError(
        'GSPLAT_TEXT_CONTEXT_UNAVAILABLE',
        'GSplatText 无法创建 2D canvas context'
      )
    }

    const font = `${fields.fontSize}px ${fields.fontFamily}`
    let textWidth: number
    try {
      context.font = font
      textWidth = Math.ceil(context.measureText(fields.text).width)
    } catch (error) {
      throw new GSplatTextError(
        'GSPLAT_TEXT_MEASURE_FAILED',
        `GSplatText measureText 失败: ${errorMessage(error)}`,
        error
      )
    }
    const textHeight = fields.fontSize
    const width = textWidth + fields.padding * 2 + strokeWidth * 2
    const height = textHeight + fields.padding * 2 + strokeWidth * 2
    this.validateDimensions(width, height, textWidth)

    let rgba: Uint8ClampedArray
    try {
      canvas.width = width
      canvas.height = height
      context.font = font
      context.textBaseline = 'middle'
      context.textAlign = 'center'
      context.clearRect(0, 0, width, height)
      const centerX = width / 2
      const centerY = height / 2
      if (strokeWidth > 0) {
        context.lineWidth = strokeWidth
        context.strokeStyle = fields.strokeStyle
        context.strokeText(fields.text, centerX, centerY)
      }
      context.fillStyle = fields.fillStyle
      context.fillText(fields.text, centerX, centerY)
    } catch (error) {
      throw new GSplatTextError(
        'GSPLAT_TEXT_DRAW_FAILED',
        `GSplatText canvas 绘制失败: ${errorMessage(error)}`,
        error
      )
    }
    try {
      rgba = context.getImageData(0, 0, width, height).data
    } catch (error) {
      throw new GSplatTextError(
        'GSPLAT_TEXT_READBACK_FAILED',
        `GSplatText canvas 像素读取失败: ${errorMessage(error)}`,
        error
      )
    }
    if (rgba.length !== width * height * 4) {
      throw new GSplatTextError(
        'GSPLAT_TEXT_READBACK_FAILED',
        `GSplatText RGBA 长度不匹配: actual=${rgba.length}, expected=${width * height * 4}`
      )
    }

    let totalSplats = 0
    for (let index = 3; index < rgba.length; index += 4) {
      if (rgba[index] > 0) totalSplats++
    }
    this.assertSnapshot(fields, operationGeneration)
    if (totalSplats === 0) {
      this.commitEmpty(fields, operationGeneration)
      return
    }

    const maxDimension = Math.max(width, height)
    const splatSize = (1 / maxDimension) * 1.1
    const adapter = this.runtime.graphicsAdapter
    let container: GSplatContainer | null = null
    try {
      try {
        const format = (this.formatValue ??= GSplatFormat.createSimpleFormat(adapter))
        container = new GSplatContainer(adapter, totalSplats, format)
      } catch (error) {
        throw new GSplatTextError(
          'GSPLAT_TEXT_ALLOCATION_FAILED',
          `GSplatText container 分配失败: ${errorMessage(error)}`,
          error
        )
      }
      this.stagedContainer = container
      this.assertSnapshot(fields, operationGeneration)

      const texelCount = container.textureDimensions.width * container.textureDimensions.height
      const centerData = new Float32Array(texelCount * 4)
      const colorData = new Uint16Array(texelCount * 4)
      const centers = container.centers
      let splatIndex = 0
      try {
        for (let pixelY = 0; pixelY < height; pixelY++) {
          for (let pixelX = 0; pixelX < width; pixelX++) {
            const pixelOffset = (pixelY * width + pixelX) * 4
            const alphaByte = rgba[pixelOffset + 3]
            if (alphaByte === 0) continue
            const x = pixelX / maxDimension - 0.5
            const y = 0
            const z = -(pixelY / maxDimension - 0.5)
            const centerOffset = splatIndex * 4
            centerData[centerOffset] = x
            centerData[centerOffset + 1] = y
            centerData[centerOffset + 2] = z
            centerData[centerOffset + 3] = splatSize
            colorData[centerOffset] = THREE.DataUtils.toHalfFloat(rgba[pixelOffset] / 255)
            colorData[centerOffset + 1] = THREE.DataUtils.toHalfFloat(rgba[pixelOffset + 1] / 255)
            colorData[centerOffset + 2] = THREE.DataUtils.toHalfFloat(rgba[pixelOffset + 2] / 255)
            colorData[centerOffset + 3] = THREE.DataUtils.toHalfFloat(alphaByte / 255)
            const sortingOffset = splatIndex * 3
            centers[sortingOffset] = x
            centers[sortingOffset + 1] = y
            centers[sortingOffset + 2] = z
            splatIndex++
          }
        }
      } catch (error) {
        throw new GSplatTextError(
          'GSPLAT_TEXT_FILL_FAILED',
          `GSplatText stream 填充失败: ${errorMessage(error)}`,
          error
        )
      }
      if (splatIndex !== totalSplats) {
        throw new GSplatTextError(
          'GSPLAT_TEXT_FILL_FAILED',
          `GSplatText splat 数量漂移: actual=${splatIndex}, expected=${totalSplats}`
        )
      }

      const centerTexture = container.getTexture('dataCenter')
      const colorTexture = container.getTexture('dataColor')
      if (!centerTexture || !colorTexture) {
        throw new GSplatTextError(
          'GSPLAT_TEXT_ALLOCATION_FAILED',
          'GSplatText simple format texture 不完整'
        )
      }
      try {
        adapter.updateTexture(centerTexture, centerData)
        adapter.updateTexture(colorTexture, colorData)
      } catch (error) {
        throw new GSplatTextError(
          'GSPLAT_TEXT_UPLOAD_FAILED',
          `GSplatText texture 上传失败: ${errorMessage(error)}`,
          error
        )
      }
      this.assertSnapshot(fields, operationGeneration)

      try {
        container.aabb.set(
          new THREE.Vector3(-0.5, -splatSize, -0.5),
          new THREE.Vector3(0.5, splatSize, 0.5)
        )
        container.update(totalSplats)
      } catch (error) {
        throw new GSplatTextError(
          'GSPLAT_TEXT_UPDATE_FAILED',
          `GSplatText container update 失败: ${errorMessage(error)}`,
          error
        )
      }
      this.assertSnapshot(fields, operationGeneration)

      const lease = container.acquire()
      container.destroy()
      container = null
      this.stagedContainer = null
      this.commitResource(fields, operationGeneration, lease)
    } finally {
      if (this.stagedContainer === container) this.stagedContainer = null
      container?.destroy()
    }
  }

  private validateDimensions(width: number, height: number, textWidth: number): void {
    if (
      !Number.isSafeInteger(textWidth) ||
      textWidth < 0 ||
      !Number.isSafeInteger(width) ||
      !Number.isSafeInteger(height) ||
      width <= 0 ||
      height <= 0 ||
      width > this.maxCanvasDimension ||
      height > this.maxCanvasDimension ||
      width * height > Number.MAX_SAFE_INTEGER / 4
    ) {
      throw new GSplatTextError(
        'GSPLAT_TEXT_INVALID_DIMENSIONS',
        `GSplatText canvas 尺寸非法: ${width}x${height}, max=${this.maxCanvasDimension}`
      )
    }
  }

  private commitEmpty(fields: GSplatTextFields, operationGeneration: number): void {
    this.assertSnapshot(fields, operationGeneration)
    if (!this.handleValue) return
    const adapter = this.runtime.graphicsAdapter
    let container: GSplatContainer | null = null
    try {
      try {
        const format = (this.formatValue ??= GSplatFormat.createSimpleFormat(adapter))
        container = new GSplatContainer(adapter, 0, format)
      } catch (error) {
        throw new GSplatTextError(
          'GSPLAT_TEXT_ALLOCATION_FAILED',
          `GSplatText empty container 分配失败: ${errorMessage(error)}`,
          error
        )
      }
      this.stagedContainer = container
      this.assertSnapshot(fields, operationGeneration)
      try {
        container.aabb.makeEmpty()
        container.update(0)
      } catch (error) {
        throw new GSplatTextError(
          'GSPLAT_TEXT_UPDATE_FAILED',
          `GSplatText empty container update 失败: ${errorMessage(error)}`,
          error
        )
      }
      this.assertSnapshot(fields, operationGeneration)
      const lease = container.acquire()
      container.destroy()
      container = null
      this.stagedContainer = null
      this.commitResource(fields, operationGeneration, lease)
    } finally {
      if (this.stagedContainer === container) this.stagedContainer = null
      container?.destroy()
    }
  }

  private commitResource(
    fields: GSplatTextFields,
    operationGeneration: number,
    resource: GSplatResourceLease
  ): void {
    let lease: GSplatResourceLease | null = resource
    try {
      this.assertSnapshot(fields, operationGeneration)
      let handle: GaussianSplatHandle
      if (this.handleValue) {
        handle = this.runtime.replaceWithResource(this.handleValue.id, lease, { name: this.name })
        lease = null
      } else {
        handle = this.runtime.createWithResource(
          Object.freeze({
            url: `procedural://gsplat-text/${this.id ?? 'generated'}`,
            name: this.name,
            ...(this.id ? { id: this.id } : {}),
          }),
          lease
        )
        lease = null
        this.stagedHandle = handle
        try {
          this.runtime.mount(handle)
        } catch (error) {
          try {
            this.runtime.disposeAsset(handle.id)
          } catch (cleanupError) {
            log.warn(`GSplatText mount 回滚失败: ${errorMessage(cleanupError)}`)
          } finally {
            this.stagedHandle = null
          }
          throw error
        }
      }
      this.assertSnapshot(fields, operationGeneration)
      this.handleValue = handle
      this.stagedHandle = null
    } catch (error) {
      lease?.dispose()
      const stagedHandle = this.stagedHandle
      this.stagedHandle = null
      if (stagedHandle && stagedHandle !== this.handleValue) {
        try {
          this.runtime.disposeAsset(stagedHandle.id)
        } catch (cleanupError) {
          log.warn(`GSplatText staged Handle 回滚失败: ${errorMessage(cleanupError)}`)
        }
      }
      throw this.toError(error, 'GSPLAT_TEXT_COMMIT_FAILED', '提交 GSplat Resource 失败')
    }
  }

  private assertSnapshot(fields: GSplatTextFields, operationGeneration = this.generation): void {
    if (
      this.disposed ||
      this.generation !== operationGeneration ||
      !fieldsEqual(fields, this.currentFieldsUnsafe())
    ) {
      throw new GSplatTextError('GSPLAT_TEXT_STALE_SNAPSHOT', 'GSplatText fields snapshot 已失效')
    }
  }

  private currentFieldsUnsafe(): GSplatTextFields {
    return {
      text: this.text,
      fontSize: this.fontSize,
      fontFamily: this.fontFamily,
      fillStyle: this.fillStyle,
      strokeStyle: this.strokeStyle,
      strokeWidth: this.strokeWidth,
      padding: this.padding,
    }
  }

  private assertActive(): void {
    if (this.disposed) {
      throw new GSplatTextError('GSPLAT_TEXT_DISPOSED', 'GSplatText 已销毁')
    }
  }

  private toError(error: unknown, code: GSplatTextErrorCode, prefix: string): GSplatTextError {
    if (error instanceof GSplatTextError) return error
    return new GSplatTextError(code, `${prefix}: ${errorMessage(error)}`, error)
  }
}
